import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { BrandMark, Button, Disclaimer, Field, Icon, TextInput, toast } from '../../components/ui';
import { errorMessage, isApiError } from '../../lib/api';
import { fmt } from '../../lib/fmt';
import { useServerNow } from '../../lib/clock';
import { useSession } from '../../state/session';

export function LoginPage({ mode }: { mode: 'login' | 'register' }) {
  const [params] = useSearchParams();
  const next = params.get('next') || '/missions';
  // An invite link (/join/CODE) sends new people here via ?next=/join/CODE — carry the code into sign-up.
  const joinCode = /^\/join\/([^/?#]+)/.exec(next)?.[1] ?? '';
  const invite = params.get('invite') ?? decodeURIComponent(joinCode);
  const nav = useNavigate();
  const status = useSession((s) => s.status);
  const load = useSession((s) => s.load);
  const login = useSession((s) => s.login);
  const register = useSession((s) => s.register);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [inviteCode, setInviteCode] = useState(invite);
  const [error, setError] = useState<string | null>(null);
  /** Field-level problems found before the request (empty or malformed fields). */
  const [invalid, setInvalid] = useState<{ name?: string; email?: string; password?: string }>({});
  /** The server refused the credentials: both fields read as invalid, the message sits under the password. */
  const [rejected, setRejected] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (status === 'unknown') void load();
    if (status === 'authenticated' && !busy) nav(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, load, nav, next]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setRejected(false);
    const problems: typeof invalid = {};
    if (mode === 'register' && !displayName.trim()) problems.name = 'Enter your name.';
    if (!email.trim()) problems.email = 'Enter your email.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) problems.email = 'Enter a full email address, like you@example.com.';
    if (!password) problems.password = 'Enter your password.';
    else if (mode === 'register' && password.length < 10) problems.password = 'Use at least 10 characters.';
    setInvalid(problems);
    if (Object.keys(problems).length) return;
    setBusy(true);
    try {
      if (mode === 'login') {
        await login(email.trim(), password);
        nav(next, { replace: true });
      } else {
        const r = await register({ email: email.trim(), password, displayName: displayName.trim(), inviteCode: inviteCode.trim() || undefined });
        if (r.joinedMissionId) toast({ title: 'You joined the shoot', tone: 'success', timeoutMs: 3000 });
        nav(r.joinedMissionId ? `/m/${r.joinedMissionId}` : `/onboarding?next=${encodeURIComponent(next)}`, { replace: true });
      }
    } catch (err) {
      setRejected(mode === 'login' && isApiError(err) && (err.status === 401 || err.status === 400));
      setError(
        isApiError(err) && err.status === 429
          ? `Too many attempts from this device — wait a few minutes, then try again.${err.message ? ` (${err.message})` : ''}`
          : mode === 'login' && isApiError(err) && err.status === 401
            ? 'That email and password don’t match. Check both and try again.'
            : errorMessage(err),
      );
    } finally {
      setBusy(false);
    }
  };

  const other = mode === 'login' ? '/register' : '/login';
  const qs = params.toString();

  const clear = (k: keyof typeof invalid) => {
    if (invalid[k]) setInvalid((v) => ({ ...v, [k]: undefined }));
    if (rejected) setRejected(false);
  };
  // A refused sign-in marks both fields; its message sits under the password, where the eye already is.
  const passwordError = invalid.password ?? (rejected ? error : null);
  const formError = rejected ? null : error;

  return (
    <div className="auth">
      <aside className="auth__brand" data-theme="dark" aria-hidden>
        <AuthBackdrop />
        <div className="auth__brand-top">
          <BrandMark size={34} />
          <div className="auth__word">
            AIRVIEW STUDIO
            <span>THE AVIATION PRODUCTION PLATFORM</span>
          </div>
        </div>
        <div className="auth__brand-mid">
          <div className="auth__modes">
            <span>PLAN</span>
            <i />
            <span>LIVE</span>
            <i />
            <span>REPLAY</span>
          </div>
          <p className="auth__lede">
            Keep your aircraft in view, between shoots and in the field. Plan together, mark the moment, and replay the flight on one UTC timeline.
          </p>
        </div>
        <AuthClock />
      </aside>

      <main className="auth__main">
        <form className="auth__form" onSubmit={submit} noValidate>
          <div className="auth__heading">
            <div className="eyebrow">{mode === 'login' ? 'Welcome back' : invite ? 'Join your crew' : 'Get started'}</div>
            <h1 className="auth__title">{mode === 'login' ? 'Sign in' : 'Create account'}</h1>
          </div>
          {invite ? (
            <div className="auth__invite" role="status" data-testid="invite-note">
              <span className="auth__invite-icon" aria-hidden>
                <Icon name="users" size={18} />
              </span>
              <div>
                <b>You’re invited to a shoot.</b>{' '}
                {mode === 'login' ? (
                  <>
                    New to Airview Studio? <Link to={`/register${qs ? `?${qs}` : ''}`}>Create an account with this invite</Link> — or sign in below to join.
                  </>
                ) : (
                  'Create your account and you’ll join it straight away.'
                )}
              </div>
            </div>
          ) : null}
          {mode === 'register' ? (
            <Field label="Name" error={invalid.name}>
              {(id) => (
                <TextInput
                  id={id}
                  autoComplete="name"
                  required
                  aria-invalid={invalid.name ? true : undefined}
                  value={displayName}
                  onChange={(e) => {
                    setDisplayName(e.target.value);
                    clear('name');
                  }}
                  placeholder="Your name"
                />
              )}
            </Field>
          ) : null}
          <Field label="Email" error={invalid.email}>
            {(id) => (
              <TextInput
                id={id}
                type="email"
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                required
                aria-invalid={invalid.email || rejected ? true : undefined}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  clear('email');
                }}
                placeholder="you@example.com"
              />
            )}
          </Field>
          <Field label="Password" error={passwordError ?? undefined} hint={mode === 'register' ? 'At least 10 characters.' : undefined}>
            {(id) => (
              <TextInput
                id={id}
                type="password"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                required
                aria-invalid={passwordError ? true : undefined}
                minLength={mode === 'register' ? 10 : undefined}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  clear('password');
                }}
              />
            )}
          </Field>
          {mode === 'register' ? (
            <Field label="Invite code" optional hint={invite && inviteCode === invite ? 'From your invite link.' : 'Have a code from a producer? Enter it to join their shoot. Otherwise we’ll help you set up next.'}>
              {(id) => <TextInput id={id} mono autoCapitalize="characters" value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} placeholder="XXXX-XXXX" />}
            </Field>
          ) : null}
          {formError ? (
            <div className="auth__alert" role="alert">
              <Icon name="alert" size={16} />
              <span>{formError}</span>
            </div>
          ) : null}
          <Button type="submit" variant="primary" size="lg" block loading={busy} iconRight="arrow-right">
            {mode === 'login' ? 'Sign in' : 'Create account'}
          </Button>
          {mode === 'login' && invite ? null : (
            <div className="auth__switch">
              {mode === 'login' ? 'New to Airview Studio?' : 'Already have an account?'}{' '}
              <Link to={`${other}${qs ? `?${qs}` : ''}`}>{mode === 'login' ? 'Create an account' : invite ? 'Sign in to join' : 'Sign in'}</Link>
            </div>
          )}
        </form>
        <footer className="auth__foot">
          <Disclaimer />
          <nav className="auth__links" aria-label="Legal">
            <Link to="/privacy">Privacy</Link>
            <Link to="/support">Support</Link>
          </nav>
        </footer>
      </main>
    </div>
  );
}

function AuthClock() {
  const now = useServerNow(1000);
  return (
    <div className="auth__clock">
      <div>
        <span className="eyebrow">UTC</span>
        <span className="num">{fmt.utc(now)}</span>
      </div>
      <div>
        <span className="eyebrow">Flight · Field · Edit</span>
        <span className="num">One shared timeline</span>
      </div>
    </div>
  );
}

/** Quiet range-ring + runway linework. */
function AuthBackdrop() {
  return (
    <svg className="auth__backdrop" viewBox="0 0 800 800" preserveAspectRatio="xMaxYMax slice">
      <g fill="none" stroke="currentColor">
        {[80, 160, 240, 320, 400, 480].map((r, i) => (
          <circle key={r} cx="560" cy="520" r={r} strokeOpacity={0.14 - i * 0.015} strokeDasharray={i % 2 ? '2 6' : undefined} />
        ))}
        <path d="M560 20v1000M60 520h1000" strokeOpacity=".08" />
        <path d="M455 600l210-160" strokeOpacity=".35" strokeWidth="10" />
        <path d="M505 430l120 190" strokeOpacity=".25" strokeWidth="7" />
        <path d="M150 700 C 300 640, 380 520, 520 470 S 720 360, 790 250" strokeOpacity=".5" strokeDasharray="1 7" strokeLinecap="round" strokeWidth="2" />
      </g>
      <g fill="currentColor">
        <circle cx="520" cy="470" r="4" />
      </g>
    </svg>
  );
}
